import { ChevronLeft, CreditCard, Info } from 'lucide-react';
import { PRIMARY_BTN } from './helper';

// Nothing on offer here — no membership plans, or the practice hasn't finished
// connecting payments. Says what happens instead rather than leaving a gap
// where the plans were, which reads as something failing to load.
const NothingOnOffer = () => (
  <div className="rounded-xl border border-gray-200 bg-secondary-50/60 p-4">
    <div className="flex items-start gap-4">
      <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-blue-100 bg-white text-teamcare-blue">
        <CreditCard size={20} />
      </span>

      <div className="min-w-0">
        <p className="text-[15px] font-bold text-secondary-900">Paying for your own care</p>
        <p className="mt-1 text-xs leading-relaxed text-secondary-500">
          You've chosen not to add insurance, so you'll be paying for your own care.
        </p>
      </div>
    </div>

    <p className="mt-4 flex items-start gap-2 border-t border-gray-200 pt-4 text-xs leading-relaxed text-secondary-500">
      <Info size={14} className="mt-0.5 shrink-0 text-secondary-400" />
      If your coverage changes, you can add insurance any time from your profile.
    </p>
  </div>
);

// Answered "no" — the heading above the plans. Header and footer are separate so
// the membership panel can keep ONE slot in the form across every state: moved
// between parents it unmounts, refetches, and flashes a plan grid at a patient
// who is already a member.
export const NoInsuranceHeader = ({ onBack, disabled = false }) => (
  <div className="animate-slide-up mb-5 flex items-start gap-3">
    <button
      type="button"
      onClick={onBack}
      disabled={disabled}
      aria-label="Back to the insurance question"
      className="mt-0.5 shrink-0 text-secondary-400 transition-colors hover:text-secondary-700 disabled:opacity-60"
    >
      <ChevronLeft size={18} strokeWidth={2.5} />
    </button>

    <div className="min-w-0">
      <p className="text-base font-bold text-secondary-900">No insurance — that's not a problem</p>
      <p className="mt-1 text-sm text-secondary-500">You can carry on and pay as you go.</p>
    </div>
  </div>
);

// The one way out of the step, shared with the member's own footer so the two
// cannot drift apart.
export const Continue = ({ label, onClick, isSubmitting = false, disabled = false }) => (
  <button
    type="button"
    onClick={onClick}
    disabled={isSubmitting || disabled}
    className={`${PRIMARY_BTN} w-full sm:w-auto`}
  >
    {isSubmitting ? <span className="loading loading-spinner loading-sm" /> : label}
  </button>
);

// Below the plans. They are an offer, not a toll gate, so the way past them is a
// real button rather than a link buried under the grid. A null label means the
// plans have not reported yet — guessing one and settling on the other changes
// the text under the patient's finger.
const NoInsurance = ({ offering = null, label = null, onContinue, isSubmitting = false, disabled = false }) => (
  <div className="animate-slide-up">
    {offering === false && <NothingOnOffer />}

    {label && (
      <div className="mt-6">
        <Continue label={label} onClick={onContinue} isSubmitting={isSubmitting} disabled={disabled} />
      </div>
    )}
  </div>
);

export default NoInsurance;
